import { Box, Typography, Divider } from '@mui/material';
import { useAssets } from '../../hooks/useAssets';

function polarToCartesian(cx, cy, r, angleDeg) {
  const rad = ((angleDeg - 90) * Math.PI) / 180;
  return { x: cx + r * Math.cos(rad), y: cy + r * Math.sin(rad) };
}

function arcPath(cx, cy, outerR, innerR, startAngle, endAngle) {
  const o1 = polarToCartesian(cx, cy, outerR, startAngle);
  const o2 = polarToCartesian(cx, cy, outerR, endAngle);
  const i1 = polarToCartesian(cx, cy, innerR, endAngle);
  const i2 = polarToCartesian(cx, cy, innerR, startAngle);
  const large = endAngle - startAngle > 180 ? 1 : 0;
  return [`M ${o1.x} ${o1.y}`, `A ${outerR} ${outerR} 0 ${large} 1 ${o2.x} ${o2.y}`,
          `L ${i1.x} ${i1.y}`, `A ${innerR} ${innerR} 0 ${large} 0 ${i2.x} ${i2.y}`, 'Z'].join(' ');
}

function DonutChart({ data, label, sub }) {
  const size = 110; const cx = 55; const cy = 55;
  const outerR = 50; const innerR = 33;
  const total = data.reduce((s, d) => s + d.value, 0);
  let angle = 0;
  return (
    <Box sx={{ position: 'relative', width: size, height: size, flexShrink: 0 }}>
      <svg width={size} height={size}>
        {total === 0
          ? <circle cx={cx} cy={cy} r={outerR} fill="none" stroke="#1e3a5f" strokeWidth={outerR - innerR} />
          : data.map(seg => {
              const sweep = (seg.value / total) * 358;
              const path = arcPath(cx, cy, outerR, innerR, angle, angle + sweep);
              angle += sweep;
              return sweep > 0.1 ? <path key={seg.name} d={path} fill={seg.color} /> : null;
            })}
      </svg>
      <Box sx={{ position: 'absolute', top: '50%', left: '50%', transform: 'translate(-50%,-50%)', textAlign: 'center', pointerEvents: 'none' }}>
        <Typography sx={{ color: '#ffffff', fontWeight: 700, fontSize: 20, lineHeight: 1 }}>{label}</Typography>
        <Typography sx={{ color: '#7a8fa6', fontSize: 9 }}>{sub}</Typography>
      </Box>
    </Box>
  );
}

function StatRow({ label, value, color, bar, barMax }) {
  return (
    <Box>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: bar ? 0.3 : 0 }}>
        <Typography sx={{ color: '#7a8fa6', fontSize: 11 }}>{label}</Typography>
        <Typography sx={{ color: color || '#ffffff', fontWeight: 700, fontSize: 12 }}>{value}</Typography>
      </Box>
      {bar && (
        <Box sx={{ height: 3, borderRadius: 2, backgroundColor: '#1e3a5f' }}>
          <Box sx={{ height: 3, borderRadius: 2, backgroundColor: color || '#00bcd4', width: `${barMax > 0 ? Math.round((bar / barMax) * 100) : 0}%`, transition: 'width 0.3s' }} />
        </Box>
      )}
    </Box>
  );
}

const TYPE_LABEL = {
  workstation: 'Workstations', server: 'Servers', firewall: 'Firewalls',
  router: 'Routers', switch: 'Switches', hypervisor: 'Hypervisors',
  vm: 'VMs', taclane: 'TACLANE', idps: 'IDPS', vuln_scanner: 'Vuln Scanners', other: 'Other',
};

const TYPE_COLOR = {
  workstation: '#00bcd4', server: '#4caf50', firewall: '#f44336',
  router: '#ff9800', switch: '#9c27b0', hypervisor: '#2196f3',
  vm: '#00bcd4', taclane: '#ffeb3b', idps: '#ff5722', vuln_scanner: '#8bc34a', other: '#607d8b',
};

export default function AssetsWidget() {
  const { summary, assets } = useAssets();

  const utilPct = summary.totalIPs > 0
    ? Math.round((summary.assigned / summary.totalIPs) * 100)
    : 0;

  // Device type breakdown
  const typeCounts = {};
  assets.forEach(a => {
    const t = a.device_type || 'other';
    typeCounts[t] = (typeCounts[t] || 0) + 1;
  });
  const typeEntries = Object.entries(typeCounts).sort((a, b) => b[1] - a[1]);

  // OS breakdown
  const osCounts = {};
  assets.forEach(a => { if (a.os) osCounts[a.os] = (osCounts[a.os] || 0) + 1; });
  const osEntries = Object.entries(osCounts).sort((a, b) => b[1] - a[1]).slice(0, 5);

  // Status donut
  const statusData = [
    { name: 'Online',     value: summary.online     || 0, color: '#4caf50' },
    { name: 'Offline',    value: summary.offline    || 0, color: '#f44336' },
    { name: 'Unassigned', value: summary.unassigned || 0, color: '#1e3a5f' },
  ];

  // Type donut (top 5 types)
  const typeDonut = typeEntries.slice(0, 6).map(([t, c]) => ({
    name: TYPE_LABEL[t] || t, value: c, color: TYPE_COLOR[t] || '#607d8b',
  }));

  return (
    <Box sx={{ p: 2, display: 'flex', flexDirection: 'column', gap: 2 }}>

      {/* Row 1: Status donut + stats + IP utilization */}
      <Box sx={{ display: 'flex', gap: 2 }}>
        <DonutChart data={statusData} label={summary.totalDevices || 0} sub="DEVICES" />

        <Box sx={{ flex: 1, display: 'flex', flexDirection: 'column', gap: 1 }}>
          <StatRow label="Online"     value={summary.online     || 0} color="#4caf50" />
          <StatRow label="Offline"    value={summary.offline    || 0} color="#f44336" />
          <StatRow label="Unassigned" value={summary.unassigned || 0} color="#4a5a70" />
          <Divider sx={{ borderColor: '#1e3a5f', my: 0.25 }} />
          <StatRow label="Total IPs"  value={summary.totalIPs   || 0} color="#7a8fa6" />
          <StatRow label="Assigned"   value={summary.assigned   || 0} color="#c0cfe0" />
          <StatRow label="IP Utilization" value={`${utilPct}%`} color="#00bcd4"
            bar={summary.assigned} barMax={summary.totalIPs} />
        </Box>
      </Box>

      <Divider sx={{ borderColor: '#1e3a5f' }} />

      {/* Row 2: Type donut + breakdown */}
      <Box sx={{ display: 'flex', gap: 2 }}>
        <DonutChart data={typeDonut} label={typeEntries.length} sub="TYPES" />

        <Box sx={{ flex: 1, display: 'flex', flexDirection: 'column', gap: 0.75 }}>
          <Typography sx={{ color: '#7a8fa6', fontSize: 10, textTransform: 'uppercase', letterSpacing: 0.5, mb: 0.25 }}>By Device Type</Typography>
          {typeEntries.map(([t, c]) => (
            <Box key={t} sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <Box sx={{ width: 6, height: 6, borderRadius: '50%', backgroundColor: TYPE_COLOR[t] || '#607d8b', flexShrink: 0 }} />
              <Typography sx={{ color: '#7a8fa6', fontSize: 11, flex: 1 }}>{TYPE_LABEL[t] || t}</Typography>
              <Typography sx={{ color: '#ffffff', fontWeight: 700, fontSize: 12 }}>{c}</Typography>
              <Box sx={{ width: 60, height: 3, borderRadius: 2, backgroundColor: '#1e3a5f' }}>
                <Box sx={{ height: 3, borderRadius: 2, backgroundColor: TYPE_COLOR[t] || '#607d8b', width: `${Math.round((c / (summary.totalDevices || 1)) * 100)}%` }} />
              </Box>
            </Box>
          ))}
        </Box>
      </Box>

      {osEntries.length > 0 && (
        <>
          <Divider sx={{ borderColor: '#1e3a5f' }} />

          {/* Row 3: OS breakdown */}
          <Box>
            <Typography sx={{ color: '#7a8fa6', fontSize: 10, textTransform: 'uppercase', letterSpacing: 0.5, mb: 1 }}>By Operating System</Typography>
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.75 }}>
              {osEntries.map(([os, c]) => (
                <Box key={os} sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <Typography sx={{ color: '#7a8fa6', fontSize: 11, flex: 1, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{os}</Typography>
                  <Typography sx={{ color: '#ffffff', fontWeight: 700, fontSize: 12, flexShrink: 0 }}>{c}</Typography>
                  <Box sx={{ width: 80, height: 3, borderRadius: 2, backgroundColor: '#1e3a5f', flexShrink: 0 }}>
                    <Box sx={{ height: 3, borderRadius: 2, backgroundColor: '#00bcd4', width: `${Math.round((c / (summary.totalDevices || 1)) * 100)}%` }} />
                  </Box>
                </Box>
              ))}
            </Box>
          </Box>
        </>
      )}

    </Box>
  );
}
